import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { convertISODateToMMDDYYYY, convertISOToTime, getTimestampDisplay } from '@/lib/utils';
import { useUserStore } from '@/stores/user';
import type { HIWorkOrderComment } from '@/types/homeInspection.types';
import { useEffect, useRef, useState } from 'react';
import Spinner from '@/images/spinner.svg';

const AVATAR_COLOR_CLASSES = [
  'bg-blue-500',
  'bg-green-600',
  'bg-purple-500',
  'bg-orange-500',
  'bg-teal-500',
  'bg-rose-500',
  'bg-indigo-500',
  'bg-amber-600',
  'bg-cyan-600',
  'bg-pink-500',
];

const getAvatarColorClass = (seed: string | undefined): string => {
  if (!seed) return 'bg-gray-400';
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLOR_CLASSES[Math.abs(hash) % AVATAR_COLOR_CLASSES.length];
};

const Comment = ({ comment }: { comment: HIWorkOrderComment }) => {
  const { timeDisplayText, hasTooltip } = getTimestampDisplay(comment.createdAt);
  const userStore = useUserStore((state) => state.user);
  const authorName = [comment.createdByFirstName, comment.createdByLastName].filter(Boolean).join(' ');
  const initial = authorName.charAt(0) || '?';

  return (
    <div className="p-4 border-bordercolor flex flex-col h-fit gap-2">
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-row gap-2 items-center">
          <span
            className={`rounded-full w-[32px] h-[32px] text-lg focus:outline-none focus-visible:outline-none text-white p-2.5 items-center justify-center flex ${getAvatarColorClass(authorName || comment.createdBy)}`}
          >
            {initial}
          </span>
          <span className="text-base font-semibold">
            {authorName}
            {userStore?.userId === comment.createdBy ? ' (you)' : ''}
          </span>
        </div>
        {hasTooltip ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <span className="text-sm text-gray-500">{timeDisplayText}</span>
              </TooltipTrigger>
              <TooltipContent>
                <span>
                  {convertISODateToMMDDYYYY(comment.createdAt)} {convertISOToTime(comment.createdAt)}
                </span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-placeholder font-normal text-[13px]">{timeDisplayText}</span>
        )}
      </div>
      <div className="text-placeholder font-normal text-sm break-all">{comment.commentText}</div>
    </div>
  );
};

const HICommentsSection = ({
  comments,
  isFetching,
}: {
  isFetching: boolean;
  comments: HIWorkOrderComment[];
}) => {
  const [lastCommentRef, setLastCommentRef] = useState<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (lastCommentRef) lastCommentRef.scrollIntoView({ behavior: 'auto' });
  }, [lastCommentRef]);

  return (
    <div className="flex flex-col h-full w-full">
      {isFetching && (
        <div className="w-full items-center flex justify-center">
          <img src={Spinner} alt="Loading..." className="h-[25px] w-[25px] animate-spin" />
        </div>
      )}

      {comments.length ? (
        <div className="overflow-auto h-[calc(100vh-245px)] custom-scrollbar" ref={containerRef}>
          {comments.map((comment, index) => {
            const isLast = index === comments.length - 1;
            return (
              <div
                key={comment.id}
                className="px-2 whitespace-pre-wrap"
                ref={isLast ? (el) => setLastCommentRef(el) : null}
              >
                <Comment comment={comment} />
                {!isLast && <hr />}
              </div>
            );
          })}
        </div>
      ) : !isFetching ? (
        <div className="w-full h-full flex justify-center items-center text-placeholder font-base">No Results</div>
      ) : null}
    </div>
  );
};

export default HICommentsSection;
